import { HttpFile } from '@truvity/rules_openapi';
import { IFileStorage } from '@truvity/widget__forms';

import { CredentialDid, WalletDid, WalletEditApi } from '../../../types';

import { ReadonlyFilesStorage } from './ReadonlyFileStorage';

export type EditableFileApi = Pick<
    WalletEditApi,
    'getPresignedDownloadUrlV1' | 'getPresignedUploadUrlV1' | 'finalizeUploadV1'
>;

interface EditableFileStorageOptions {
    readonly walletDid: WalletDid;
    readonly entityDid: WalletDid;
    readonly draftId: CredentialDid;
    readonly fileApi: EditableFileApi;
}

export class EditableFileStorage extends ReadonlyFilesStorage implements IFileStorage {
    constructor(private options: EditableFileStorageOptions) {
        super(options.fileApi, options.walletDid, options.entityDid);
    }

    storeFile = async (file: HttpFile, _fieldName: string, onProgress?: (event: ProgressEvent) => void) => {
        const { FileDid, UploadURL } = await this.options.fileApi.getPresignedUploadUrlV1(
            this.options.walletDid,
            this.options.entityDid,
        );

        await upload({
            file,
            method: 'PUT',
            url: UploadURL,
            onProgress,
        });

        await this.options.fileApi.finalizeUploadV1(FileDid, this.options.walletDid, this.options.entityDid);

        return FileDid;
    };

    // Our API does not support deleting files
    deleteFile = async () => {};
}

// Utils

interface UploadOptions {
    file: HttpFile;
    url: string;
    method: 'POST' | 'PUT';
    onProgress?: (event: ProgressEvent) => void;
}

async function upload({ file, method, url, onProgress }: UploadOptions) {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        if (onProgress) xhr.upload.onprogress = onProgress;

        const onFinish = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                resolve(null);
            } else {
                reject(new Error('Failed to upload file'));
            }
        };

        xhr.onload = onFinish;
        xhr.onerror = onFinish;
        xhr.open(method, url, true);
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.send(file);
    });
}
